import ExpeditionChapterHeading from "./ExpeditionChapterHeading";
import IconLink from "./IconLink";
import { siteUrl } from "./site";
import { expeditionLinks } from "./expeditionLinks";

const modelStructureCode = `// .model을 읽을 때 모델 종류와 이어지는 데이터 개수를 확인합니다.
using ModelDataHeader = struct tagModelDataHeader
{
    bool   isSkeletal;
    uint32 maxTextureSize; // 텍스처 타입 수
    uint32 numMaterials;
    uint32 numMeshes;
    uint32 numBones;
};

using ModelMeshData = struct tagModelMeshHeader
{
    string      name;
    uint32      materialIndex{}; // 이 메시가 사용하는 재질
    uint32      numVertices{};
    uint32      numIndices{};
    uint32      numBoneRefs{};   // 메시가 참조하는 본 개수

    // 정적: VTXMESH / 스켈레탈: VTXANIMMESH
    // 정점별 blendIndex·blendWeight는 VTXANIMMESH에 들어갑니다.
    void*       vertices;
    void*       indices;
    void*       boneIndices;    // 메시의 본 참조 목록
    void*       offsetMatrices; // 각 참조 본에 대응하는 오프셋
};

using ModelBoneData = struct tagModelBone
{
    string      name;
    int32       parentBoneIndex{ -1 }; // 부모 인덱스로 본 계층 구성
    Matrix      localTransform{};
    Matrix      offsetMatrix{};
};`;

const materialStructureCode = `using ModelMaterialData = struct tagModelMaterialData
{
    string      name;

    Vec4        diffuseColor{};
    Vec4        ambientColor{};
    Vec4        specularColor{};
    Vec4        emissiveColor{};

    float       shininess{};
    float       opacity{ 1.f };
    float       roughness{ 0.5f };
    float       metalness{ 0.f };

    // 재질에 연결된 ModelTextureSlot 목록
    uint32      numSlots{};
    void*       modelTextureSlots;
};

using ModelTextureSlot = struct tagModelTextureSlot
{
    string      path; // 텍스처 파일 경로

    uint32      typeIndex{ 0 };    // Assimp 텍스처 타입
    uint32      textureIndex{ 0 }; // 해당 타입 안의 텍스처 인덱스

    // 텍스처와 함께 추출하는 UV·블렌딩 정보
    Vec2        uvScale{ 1.f, 1.f };
    Vec2        uvOffset{ 0.f, 0.f };
    float       uvRotation{ 0.f };
    float       blendFactor{ 1.f };
    uint32      blendOp{};
    uint32      uvChannel{};
    uint32      wrapMode{};
};`;

const animationStructureCode = `using ModelAnimationData = struct tagModelAnimationData
{
    string      name;

    double      duration{};       // Tick 기준 재생 길이
    double      ticksPerSecond{};
    uint32      numChannels{};    // 이 클립에 포함된 채널 수
    Vec3        rootTotalTranslation{}; // 클립 전체의 루트 이동량
    Vec4        rootTotalRotation{};    // 클립 전체의 루트 회전량
};

using ModelChannelData = struct tagChannelData
{
    string      boneName;

    int32       boneIndex{ -1 }; // 채널이 적용될 모델의 본
    uint32      numKeyframes{};
    void*       keyframes;      // KEYFRAME 배열: 시간·이동·회전·크기
};

// 클립의 전체 루트 이동·회전량과 별개로,
// 재생 중 프레임 변위는 Channel에서 계산합니다.`;

const rootMotionCode = `if (_boneIndex == rootNodeIndex)
    Update_Velocity(transform, currentTrackPosition);

XMMATRIX targetMatrix;

if (_boneIndex == rootNodeIndex)
{
    /* 루트 본의 Translation을 0으로 강제하여 제자리 유지 */
    targetMatrix = XMMatrixAffineTransformation(
        transform.scale,
        XMVectorZero(),
        transform.rotation,
        Vec3::Zero
    );
}`;

const rootDeltaCode = `_frameDelta.position = curTransform.position - _prevPos;

Quat invPrevRot = _prevRot;
invPrevRot.Conjugate();
_frameDelta.rotation = curTransform.rotation * invPrevRot;`;

function SourceLink({ path }: { path: string }) {
  return <IconLink href={`${expeditionLinks.source}/blob/main/${path}`} target="_blank" rel="noopener noreferrer">전체 코드 보기 ↗</IconLink>;
}

export default function ExpeditionModelPipeline() {
  return <section id="model" className="expedition-case expedition-pipeline" aria-labelledby="expedition-model-title">
    <div className="container">
      <header className="expedition-pipeline-header">
        <ExpeditionChapterHeading number="01" label="RESOURCE / ANIMATION PIPELINE" id="expedition-model-title" title="모델·애니메이션 파이프라인" technology="C++ · Assimp · UE5 · ImGui · DirectX 11" />
        <div className="expedition-pipeline-lead"><p>여러 소스의 애니메이션을 캐릭터 메시에 맞춰 사용하기 위해, UE5에서 리타겟·편집한 결과를 자체 엔진에 가져오는 파이프라인을 만들었습니다. ModelEditor 안에서 FBX 변환과 재생 확인을 진행하고, Engine이 읽은 데이터를 Client에서 활용했습니다.</p></div>
      </header>

      <div className="expedition-pipeline-overview" id="model-structure">
        <h4>전체 구조</h4>
        <div className="expedition-pipeline-architecture" aria-label="UE5 보정 결과를 자체 포맷으로 변환하고 Engine이 읽어 Model Editor와 Client에서 사용하는 구조">
          <p className="expedition-pipeline-input">입력 리소스 <span>캐릭터 메시 · 여러 소스의 애니메이션</span></p>
          <ol className="expedition-pipeline-route">
            <li className="expedition-pipeline-tool"><span className="expedition-pipeline-node-type">리타겟·편집</span><strong>UE5</strong><p>캐릭터에 동작 적용<br />속도 조정·동작 합성</p></li>
            <li className="expedition-pipeline-artifact"><strong>FBX</strong><span>편집 결과 Export</span></li>
            <li className="expedition-pipeline-tool"><span className="expedition-pipeline-node-type">ModelEditor 내부 변환</span><strong>ModelConverter</strong><p>메시·본·애니메이션 추출</p><small>C++ · Assimp</small></li>
            <li className="expedition-pipeline-artifact"><strong>.model / .anim</strong><span>바이너리 파일</span></li>
            <li className="expedition-pipeline-tool"><span className="expedition-pipeline-node-type">런타임 로드</span><strong>Engine</strong><p>모델·애니메이션 구성</p><small>C++ · 역직렬화</small></li>
          </ol>
          <div className="expedition-pipeline-consumers"><p>ModelEditor 프로젝트에 변환기와 뷰어를 함께 구성 · 뷰어와 Client는 Engine의 모델·애니메이션 기능 사용</p><div><article><h5>ModelEditor · 뷰어</h5><span>ImGui · DirectX 11</span><p>모델 표시 · 애니메이션 재생<br />루트본 선택 · Root Motion 확인</p></article><article><h5>Client</h5><span>캐릭터 동작에 연결</span><p>Engine이 읽은 결과 확인<br />인게임 모델·애니메이션 활용</p></article></div></div>
        </div>
      </div>

      <div className="expedition-pipeline-block" id="model-retarget">
        <div><h4>리타겟과<br className="expedition-pipeline-heading-break" /> 애니메이션 편집</h4><p className="expedition-pipeline-stage-tech">UE5 · 리타겟 · FBX Export</p></div>
        <div className="expedition-pipeline-copy"><p>가장 오래 걸린 작업은 여러 애니메이션 데이터를 사용할 캐릭터 메시에 맞추는 일이었습니다. 확보한 원본 데이터를 자체 엔진으로 옮기는 것만으로는 원하는 동작을 적용하기 어려워, 게임에 가져오기 전에 애니메이션을 맞추는 과정이 필요했습니다.</p>
          <dl className="expedition-pipeline-decisions"><div><dt>대상 메시 적용</dt><dd>UE5에서 애니메이션을 사용할 캐릭터 메시로 리타겟하고 동작을 보정했습니다.</dd></div><div><dt>동작 편집</dt><dd>애니메이션의 속도와 블렌딩 가중치를 조정하고, 여러 애니메이션을 섞어 필요한 하나의 동작으로 만들었습니다.</dd></div><div><dt>스키닝 보정</dt><dd>메시 정점이 본을 따라가는 스키닝 가중치도 조정해, 대상 캐릭터에 동작을 적용했습니다.</dd></div><div><dt>자체 엔진에 전달</dt><dd>편집한 결과를 FBX로 내보낸 뒤 ModelConverter로 추출했습니다. UE에서 준비한 동작을 자체 엔진으로 가져오는 작업 흐름을 구성했습니다.</dd></div></dl>
        </div>
      </div>

      <div className="expedition-pipeline-block" id="model-conversion">
        <div><h4>리소스 변환</h4><p className="expedition-pipeline-stage-tech">C++ · Assimp</p></div>
        <div className="expedition-pipeline-copy"><p>ModelEditor에 포함된 ModelConverter는 Assimp로 FBX를 읽고, 자체 엔진이 사용할 메시·재질·본·애니메이션 데이터로 나눕니다. 왼손 좌표계 변환과 삼각형 구성, 노멀·탄젠트 생성, 정점별 본 가중치 제한 등의 전처리를 거쳐 Engine_Model_Struct.h에 정의한 데이터로 구성했습니다.</p>
          <details className="expedition-evidence"><summary>변환 데이터 구성 보기</summary>
            <figure className="expedition-pipeline-code"><figcaption>모델 헤더 · 메시 · 본 계층</figcaption><pre><code>{modelStructureCode}</code></pre></figure>
            <figure className="expedition-pipeline-code"><figcaption>재질 · 텍스처 슬롯</figcaption><pre><code>{materialStructureCode}</code></pre></figure>
            <figure className="expedition-pipeline-code"><figcaption>애니메이션 클립 · 본별 채널</figcaption><pre><code>{animationStructureCode}</code></pre></figure>
            <div className="expedition-pipeline-code-footer"><p>Engine_Model_Struct.h에서 관련 선언을 발췌하고 설명 주석을 덧붙였습니다. Export할 때는 문자열·배열의 실제 내용을 기록하고, Engine에서 같은 순서로 읽습니다.</p><SourceLink path="Engine/Public/Engine_Model_Struct.h" /></div>
          </details>

        </div>
      </div>

      <div className="expedition-pipeline-block" id="model-runtime">
        <div><h4>데이터 Export와<br className="expedition-pipeline-heading-break" /> Engine 로드</h4><p className="expedition-pipeline-stage-tech">C++ · 바이너리 직렬화·역직렬화</p></div>
        <div className="expedition-pipeline-copy"><p>모델과 애니메이션은 각각 <code>.model</code>, <code>.anim</code> 바이너리 파일로 Export했습니다. Engine은 FBX 원본 대신 이 파일을 읽어 모델 컴포넌트와 애니메이션 객체를 구성합니다. 변환기에서 기록하는 데이터와 Engine이 읽는 데이터의 구성을 맞춰 도구와 런타임을 연결했습니다.</p>
          <div className="expedition-pipeline-files"><article><h5>.model</h5><p>정적·스켈레탈 구분과 데이터 개수, 재질, 메시, 본 계층을 저장합니다.</p><span>Export_ModelData → Ready_Model</span></article><article><h5>.anim</h5><p>애니메이션별 파일에 재생 길이·Tick 정보, 루트 이동·회전량, 채널과 키프레임을 저장합니다.</p><span>Export_AnimationData → Ready_Animation</span></article></div>
          <p>이 파일에는 원본에서 추출한 키프레임을 저장합니다. 루트본의 이동 성분을 포즈에서 분리하는 처리는 Engine의 재생 단계에서 수행합니다.</p>
        </div>
      </div>

      <div className="expedition-pipeline-block" id="model-preview">
        <div><h4>변환 결과 확인과<br className="expedition-pipeline-heading-break" /> Root Motion 활용</h4><p className="expedition-pipeline-stage-tech">애니메이션 재생 · 루트본 지정 · 이동량 분리</p></div>
        <div className="expedition-pipeline-copy"><p>ModelEditor 안에서 변환한 모델과 애니메이션을 불러와 동작을 확인했습니다. 애니메이션을 선택해 재생하고, 루트로 사용할 본을 지정해 이동 성분이 분리된 결과도 확인할 수 있게 했습니다. 뷰어와 Client는 모두 Engine의 ModelCom으로 데이터를 읽고 재생하므로, 에디터에서 확인한 데이터를 인게임 캐릭터에 활용할 수 있도록 연결했습니다.</p>
          <figure className="expedition-frame"><IconLink href={siteUrl('/images/expedition-33/editor-root-motion.gif')} target="_blank" rel="noopener noreferrer"><img src={siteUrl('/images/expedition-33/editor-root-motion.gif')} alt="ModelEditor에서 애니메이션을 재생하며 Root Motion 적용 결과를 확인하는 시연" width="1244" height="844" loading="lazy" /></IconLink><figcaption>ModelEditor에서 Root Motion을 적용하며 애니메이션의 이동 성분을 확인하는 시연.</figcaption></figure>
          <h5 className="expedition-pipeline-subtitle" id="model-root-motion">본의 이동을 분리해 캐릭터 이동에 활용</h5>
          <p>Client에서 애니메이션의 이동량을 사용할 수 있도록, Engine의 재생 단계에서 Root Motion을 분리했습니다. <code>Set_LocalRootNode</code>에 본 인덱스를 전달하면 해당 본을 기준으로 처리합니다. 모델마다 사용할 본을 지정할 수 있고, 원본 키프레임은 유지한 채 재생할 때 이동 성분을 분리합니다.</p>
          <div className="expedition-pipeline-mapping" aria-label="선택한 본을 루트로 지정하고 프레임 변위를 추출한 뒤 본 포즈의 이동 성분을 0으로 처리"><span>루트로 사용할 본 지정</span><span>프레임 변위 추출</span><strong>본 포즈의 이동 성분 제거</strong></div>
          <p>지정한 본은 원래의 변환값으로 프레임 변위를 먼저 계산한 뒤, 본 행렬을 만들 때 이동값을 0으로 처리합니다. 크기와 회전은 유지하고 위치 이동만 분리하므로, 분리한 이동량을 캐릭터의 이동 처리에서 사용할 수 있습니다.</p>
          <details className="expedition-evidence"><summary>루트본의 이동 성분 분리 코드 보기</summary><figure className="expedition-pipeline-code"><figcaption>지정한 본의 변위를 추출하고 포즈에서 이동 성분 제거</figcaption><pre><code>{rootMotionCode}</code></pre><div className="expedition-pipeline-code-footer"><p>Channel::Update_TransformationMatrix 발췌. 지정한 루트본에만 이 처리를 적용하고, 나머지 본은 원래 위치값으로 행렬을 구성합니다.</p><SourceLink path="Engine/Private/Channel.cpp" /></div></figure></details>
          <details className="expedition-evidence"><summary>프레임 이동·회전량 계산 코드 보기</summary><figure className="expedition-pipeline-code"><figcaption>이전 프레임과의 차이를 별도 데이터로 보관</figcaption><pre><code>{rootDeltaCode}</code></pre><div className="expedition-pipeline-code-footer"><p>Channel::Update_Velocity 발췌. 위치 차이와 회전 차이를 저장하고, ModelCom의 Get_RootTransformVelocity를 통해 제공합니다.</p><SourceLink path="Engine/Private/Channel.cpp" /></div></figure></details>
          <p>분리한 프레임 이동량은 ModelCom의 <code>Get_RootTransformVelocity</code>로 제공합니다. Client의 <code>PlayableParts::Get_RootMotionPosition</code>은 여기서 위치 변위를 가져와 캐릭터 동작에 사용할 수 있도록 전달합니다. 본 포즈의 이동과 캐릭터의 실제 이동을 나눠 처리할 수 있게 한 구조입니다.</p>
          <p>준비한 애니메이션은 캐릭터 상태와 Animation Preset에 연결해 활용했습니다. 실제 월드 캐릭터의 상태 처리와 루트 이동 진행 비율은 다음 항목에서 설명합니다.</p>
          <IconLink className="expedition-pipeline-next" href="#interaction">State Machine 구성과 Animation Preset →</IconLink>
        </div>
      </div>
    </div>
  </section>;
}
